import { create } from 'zustand'
import { integrationsApi, type DecidedCrmExport, type PendingCrmExport } from '@/lib/api'
import { toast } from 'sonner'

interface CrmExportApprovalsStore {
  pending: PendingCrmExport[]
  decided: DecidedCrmExport[]
  loading: boolean

  fetchPending: () => Promise<void>
  fetchDecided: () => Promise<void>
  approve: (requestId: number) => Promise<void>
  reject: (requestId: number, reason?: string) => Promise<void>
  reset: () => void
}

const initialState = {
  pending: [] as PendingCrmExport[],
  decided: [] as DecidedCrmExport[],
  loading: false,
}

export const useCrmExportApprovalsStore = create<CrmExportApprovalsStore>((set, get) => ({
  ...initialState,

  // Loads the decided list alongside: every approver is notified of a request,
  // so whoever didn't act needs to see who did.
  fetchPending: async () => {
    set({ loading: true })
    void get().fetchDecided()
    try {
      const response = await integrationsApi.crmExportRequests()
      set({ pending: response.data, loading: false })
    } catch {
      // Expected for any role other than Owner/Admin/Manager (403) -- the
      // section never calls this without checking usePermission first, so a
      // failure here is unusual, not routine.
      set({ loading: false })
    }
  },

  fetchDecided: async () => {
    try {
      const response = await integrationsApi.crmExportDecided()
      set({ decided: response.data })
    } catch {
      // Secondary to the pending queue; leave the last list in place.
    }
  },

  approve: async (requestId: number) => {
    try {
      const response = await integrationsApi.approveCrmExport(requestId)
      set({ pending: get().pending.filter((p) => p.id !== requestId) })
      void get().fetchDecided()
      // Approving IS the upload -- report what actually landed, not just that
      // the request was released. Skipped rows (no email) and per-contact CRM
      // failures are both normal outcomes of a successful batch.
      const r = response.data
      const parts = [`Exported ${r.exported} contact${r.exported === 1 ? '' : 's'} to ${r.provider}`]
      if (r.failed > 0) parts.push(`${r.failed} failed`)
      if (r.skipped > 0) parts.push(`${r.skipped} skipped (no email)`)
      if (r.failed > 0) toast.warning(parts.join(', '))
      else toast.success(parts.join(', '))
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve the export')
      // Another approver may have decided it first -- re-read so it moves out
      // of the pending list and shows who did.
      if (error.response?.status === 400) void get().fetchPending()
      throw error
    }
  },

  reject: async (requestId: number, reason?: string) => {
    try {
      await integrationsApi.rejectCrmExport(requestId, reason)
      set({ pending: get().pending.filter((p) => p.id !== requestId) })
      void get().fetchDecided()
      toast.success('Export request rejected')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject the export')
      if (error.response?.status === 400) void get().fetchPending()
      throw error
    }
  },

  reset: () => set(initialState),
}))
